import { Chart, useChart } from '@chakra-ui/charts';
import { Box, Center, For, HStack, Text, useToken, VStack } from '@chakra-ui/react';
import ChartWrapper from '@components/ui/chart/ChartWrapper';
import { targetColor } from '@utils/map/map.constants';
import { formatUnit, OtherUnits } from '@utils/unit.utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { Label, Pie, PieChart, PieLabelRenderProps, Sector, Tooltip, TooltipContentProps } from 'recharts';

import { ClassificationEnum } from '@/types/sensor/classification.types';

type Props = {
  dronesWithFlightPlansCount: number;
  dronesCount: number;
};

const CustomPieTooltip = ({ active, payload }: TooltipContentProps) => {
  const isVisible = active && payload.length !== 0;
  if (!isVisible) {
    return;
  }
  return (
    <Center paddingY={1} paddingX={1.5} backgroundColor="neutral.black">
      <Text>{Number(payload[0].value)}</Text>
    </Center>
  );
};

export default function DronesInFlightPlansPie({ dronesWithFlightPlansCount, dronesCount }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [neutral500, neutral600] = useToken('colors', ['neutral.500', 'neutral.600']);

  const chart = useChart({
    data: [
      {
        name: formatMessage({ id: 'components.notificationSearch.reporting.withFp' }),
        value: dronesWithFlightPlansCount,
        color: targetColor[ClassificationEnum.FRIEND],
      },
      {
        name: formatMessage({ id: 'components.notificationSearch.reporting.withoutFp' }),
        value: dronesCount - dronesWithFlightPlansCount,
        color: neutral600,
      },
    ],
  });

  const CustomizedPieLabel = ({ x, y, value }: PieLabelRenderProps) => {
    const [fontSmall] = useToken('fontSizes', ['xs']);
    if (value === 0) {
      return;
    }
    return (
      <text
        x={x}
        y={y}
        fill={neutral500}
        style={{ fontSize: fontSmall }}
        textAnchor="middle"
        dominantBaseline="central"
      >
        {formatUnit(OtherUnits.PERCENT, (value / chart.getTotal('value')) * 100, { precision: 1 })}
      </text>
    );
  };

  const CustomLabel = ({ total }: Readonly<{ total: number }>) => {
    return (
      <foreignObject width="100%" height="100%">
        <Center width="100%" height="100%">
          <VStack gap={0.25}>
            <Text fontSize={24} fontWeight="medium">
              {total}
            </Text>
            <Text color="neutral.300" whiteSpace="pre-line" textAlign="center">
              <FormattedMessage id="components.notificationSearch.reporting.totalDetected" />
            </Text>
          </VStack>
        </Center>
      </foreignObject>
    );
  };

  return (
    <ChartWrapper
      width="410px"
      isEmpty={chart.getTotal('value') === 0}
      emptyLabel={formatMessage({ id: 'components.notificationSearch.reporting.noDetection' })}
      title={formatMessage({ id: 'components.notificationSearch.reporting.nbDronesInFlightplans' })}
    >
      <Chart.Root chart={chart} position="relative">
        <PieChart width="100%" height={348}>
          <Tooltip cursor={true} isAnimationActive={false} content={(props) => <CustomPieTooltip {...props} />} />
          <Pie
            innerRadius={102}
            outerRadius={124}
            startAngle={90}
            endAngle={-270}
            data={chart.data}
            label={CustomizedPieLabel}
            nameKey="name"
            strokeWidth={0}
            shape={(props) => <Sector {...props} fill={chart.color(props.payload.color)} />}
          />
          <Label position="center" content={() => <CustomLabel total={chart.getTotal('value')} />} />
        </PieChart>
      </Chart.Root>
      <HStack gap={5} width="100%" justifyContent="center">
        <For each={chart.data}>
          {(entry) => (
            <HStack key={entry.value} gap={1}>
              <Box
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  backgroundColor: entry.color,
                }}
              />
              <Text textStyle="sm">{entry.name}</Text>
            </HStack>
          )}
        </For>
      </HStack>
    </ChartWrapper>
  );
}
